import { FaMoon } from "react-icons/fa";
import { FiClock } from "react-icons/fi";
import type { TFunction } from "i18next";
import Spinner from "../../../components/Spinner";
import type { DateInputValue, NoctisReachability } from "../types/dashboard.types";
import { formatCompactDateTime } from "../utils/dashboardDate.utils";
import { healthBadgeClass } from "../utils/dashboardStatus.utils";

interface NoctisActionTileProps {
  loading: boolean;
  error: string | null;
  reachability: NoctisReachability | null;
  queueTotal: number;
  lastSuccessAt: DateInputValue;
  nextAttemptAt: DateInputValue;
  locale: string;
  onClick: () => void;
  t: TFunction;
}

const NoctisActionTile = ({ loading, error, reachability, queueTotal, lastSuccessAt, nextAttemptAt, locale, onClick, t }: NoctisActionTileProps) => {
  const enabled = reachability?.enabled ?? reachability?.configured ?? null;
  const reachable = reachability?.reachable;
  const authorized = reachability?.authorized;
  const checking = loading || reachability == null || reachable == null || authorized == null;
  const ok = !checking && enabled !== false && reachable === true && authorized !== false && !error;
  const authFail = !checking && reachable === true && authorized === false;
  const status = checking ? "warning" : enabled === false ? "disabled" : error || authFail ? "error" : ok ? "online" : "offline";

  return (
    <div className="rounded-[24px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_12px_30px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-950/60">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-stretch xl:justify-between">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <FaMoon className="text-2xl text-emerald-600" />
            <span>{t("dashboard.noctis.label", { defaultValue: "Noctis" })}</span>
          </p>
          {loading && reachability == null ? (
            <div className="mt-2 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-300">
              <Spinner />
              {t("common.loading", { defaultValue: "Loading..." })}
            </div>
          ) : error ? (
            <p className="mt-2 text-sm text-red-600 dark:text-red-300">{error}</p>
          ) : (
            <>
              <p className="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">{queueTotal}</p>
              <div className="mt-3 space-y-1 text-sm text-slate-700 dark:text-slate-200">
                <div className="flex min-w-0 items-center justify-between gap-3">
                  <span className="min-w-0 flex-1 truncate">{t("dashboard.noctis.queue", { defaultValue: "In queue" })}</span>
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-100">{queueTotal}</span>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex min-w-0 flex-col items-end justify-between gap-3 xl:min-w-[140px]">
          <div className="ml-auto w-full text-right text-xs font-semibold text-slate-500 dark:text-slate-300">
            <div className="truncate">{formatCompactDateTime(lastSuccessAt, locale)}</div>
            <div className="mt-1 flex items-center justify-end gap-1 text-[11px]">
              <FiClock className="shrink-0" />
              <span className="truncate">{formatCompactDateTime(nextAttemptAt, locale)}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClick}
            disabled={loading}
            className="inline-flex max-w-full shrink items-center justify-center rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition hover:-translate-y-0.5 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 motion-reduce:transform-none self-stretch xl:self-auto"
          >
            <span className="truncate">{t("common.open", { defaultValue: "Open" })}</span>
            <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold">{queueTotal}</span>
          </button>
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${healthBadgeClass(status)}`}>
            {status === "online"
              ? t("status.online", { defaultValue: "Online" })
              : status === "warning"
                ? t("status.warning", { defaultValue: "Warning" })
                : status === "disabled"
                  ? t("status.disabled", { defaultValue: "Disabled" })
                  : status === "offline"
                    ? t("status.offline", { defaultValue: "Offline" })
                    : t("status.error", { defaultValue: "Error" })}
          </span>
        </div>
      </div>
    </div>
  );
};

export default NoctisActionTile;
